/**
 * Training streak badge with flame icon and current count.
 * Shows consecutive days with activity or planned rest.
 */

import { useTranslation } from 'react-i18next';
import { Flame } from 'lucide-react';
import { GlassCard } from '@/components/ui/GlassCard';
import { CardContent } from '@/components/ui/layout/card';
import { useAuthenticatedQuery } from '@/hooks/useAuthenticatedQuery';
import { fetchStreak, type StreakData } from '@/lib/analyticsApi';

export function StreakBadge() {
  const { t } = useTranslation();

  const { data, isLoading } = useAuthenticatedQuery<StreakData>({
    queryKey: ['analytics', 'streak'],
    queryFn: fetchStreak,
    staleTime: 5 * 60 * 1000,
    gcTime: 15 * 60 * 1000,
  });

  if (isLoading || !data) return null;

  // Don't show if no streak
  if (data.current_streak === 0) return null;

  const flameColor = data.current_streak >= 7 ? '#FF6B2B' : data.current_streak >= 3 ? '#FFB43C' : '#A0A0A0';

  return (
    <GlassCard className="border-white/[0.06]">
      <CardContent className="p-4 flex items-center gap-3">
        <Flame
          className="h-7 w-7 shrink-0"
          style={{ color: flameColor }}
          fill={data.current_streak >= 3 ? flameColor : 'none'}
        />

        <div className="min-w-0">
          <div className="flex items-baseline gap-1.5">
            <span className="text-lg font-mono font-semibold text-foreground">
              {data.current_streak}
            </span>
            <span className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
              {t('dashboard.streak.days', { defaultValue: 'day streak' })}
            </span>
          </div>
          {data.longest_streak > data.current_streak && (
            <span className="text-[10px] text-muted-foreground/60 font-mono">
              {t('dashboard.streak.best', {
                defaultValue: 'Best: {{best}} days',
                best: data.longest_streak,
              })}
            </span>
          )}
        </div>
      </CardContent>
    </GlassCard>
  );
}
